import { Skeleton } from "@/components/ui/skeleton";

interface ClientDealSkeletonProps {
  embedded?: boolean;
}

// Mirrors ClientDealView's editorial client surface: address block + progress
// card row, the Current Chapter banner, then the [1fr_380px] grid. Uses the
// linen/bone/edge palette the page renders on, not the dashboard tokens.
// The optional 16/9 hero is deliberately omitted — it renders only when the
// deal has an image, so reserving space for it would shift more often than not.
export default function ClientDealSkeleton({ embedded = false }: ClientDealSkeletonProps) {
  return (
    <section
      className={embedded ? "" : "min-h-screen bg-linen"}
      role="status"
      aria-label="Loading your deal"
    >
      <div
        className={
          embedded
            ? "mx-auto max-w-3xl pb-24 lg:max-w-6xl"
            : "mx-auto max-w-3xl px-6 pb-40 pt-14 lg:max-w-6xl lg:px-8 lg:pt-24"
        }
      >
        <div className="mb-9 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0 flex-1">
            <Skeleton className="h-3 w-40 bg-ink/5" />
            <Skeleton className="mt-4 h-10 w-4/5 max-w-xl bg-ink/5" />
            <Skeleton className="mt-3 h-4 w-2/5 max-w-sm bg-ink/5" />
          </div>
          <Skeleton className="h-28 w-full shrink-0 bg-ink/5 lg:w-64" />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_380px]">
          <div className="flex flex-col gap-7">
            <div className="border border-edge bg-white p-6 lg:p-7">
              <Skeleton className="h-3 w-36 bg-ink/5" />
              <Skeleton className="mt-3 h-7 w-3/5 bg-ink/5" />
              <Skeleton className="mt-4 h-4 w-full bg-ink/5" />
              <Skeleton className="mt-2 h-4 w-4/5 bg-ink/5" />
            </div>

            <div className="border border-edge bg-white p-6 lg:p-7">
              <Skeleton className="h-3 w-28 bg-ink/5" />
              {[0, 1, 2].map((row) => (
                <div key={row} className="mt-5 flex items-start gap-3">
                  <Skeleton className="size-5 shrink-0 rounded-full bg-ink/5" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/5 bg-ink/5" />
                    <Skeleton className="h-3 w-32 bg-ink/5" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <div className="border border-edge bg-white p-6">
              <Skeleton className="h-3 w-24 bg-ink/5" />
              <Skeleton className="mt-4 size-14 rounded-full bg-ink/5" />
              <Skeleton className="mt-4 h-4 w-32 bg-ink/5" />
              <Skeleton className="mt-2 h-3 w-40 bg-ink/5" />
            </div>
            <div className="border border-edge bg-white p-6">
              <Skeleton className="h-3 w-20 bg-ink/5" />
              {[0, 1, 2, 3].map((row) => (
                <div key={row} className="mt-5">
                  <Skeleton className="h-3 w-16 bg-ink/5" />
                  <Skeleton className="mt-2 h-4 w-4/5 bg-ink/5" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <span className="sr-only">Loading your deal…</span>
    </section>
  );
}
